import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Paperclip, X, FileText, Image, File } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fetch upload limits once and cache module-wide (keeps UI in sync with backend env)
let cachedLimits = null;
let limitsPromise = null;
const DEFAULT_LIMITS = { default_mb: 50, html_mb: 150, html_exts: ['.html', '.htm'] };
function fetchLimits() {
  if (cachedLimits) return Promise.resolve(cachedLimits);
  if (!limitsPromise) {
    limitsPromise = axios.get(`${API_URL}/config/upload-limits`)
      .then(r => { cachedLimits = r.data || DEFAULT_LIMITS; return cachedLimits; })
      .catch(() => DEFAULT_LIMITS);
  }
  return limitsPromise;
}

const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
const pdfExts = ['.pdf'];
const wordExts = ['.doc', '.docx'];
const excelExts = ['.xls', '.xlsx'];
const htmlExts = ['.html', '.htm'];

function isImage(name) { return imageExts.some(ext => name.toLowerCase().endsWith(ext)); }
function isPDF(name) { return pdfExts.some(ext => name.toLowerCase().endsWith(ext)); }
function isWord(name) { return wordExts.some(ext => name.toLowerCase().endsWith(ext)); }
function isExcel(name) { return excelExts.some(ext => name.toLowerCase().endsWith(ext)); }
function isHTML(name) { return htmlExts.some(ext => name.toLowerCase().endsWith(ext)); }

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function FilePreviewThumb({ file, onRemove, index, limitMb }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const overLimit = limitMb && file.size > limitMb * 1024 * 1024;

  const handleRemove = () => onRemove(index);

  useEffect(() => {
    if (isImage(file.name) || isPDF(file.name)) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  const renderPreview = () => {
    if (isImage(file.name) && previewUrl) {
      return (
        <img
          src={previewUrl}
          alt={file.name}
          className="w-full h-24 object-cover rounded-t-md"
        />
      );
    }
    if (isPDF(file.name) && previewUrl) {
      return (
        <div className="w-full h-24 bg-red-50 flex flex-col items-center justify-center rounded-t-md border-b border-red-100">
          <svg className="h-10 w-10 text-red-500 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <span className="text-xs font-bold text-red-600">PDF</span>
        </div>
      );
    }
    if (isWord(file.name)) {
      return (
        <div className="w-full h-24 bg-blue-50 flex flex-col items-center justify-center rounded-t-md border-b border-blue-100">
          <svg className="h-10 w-10 text-blue-600 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <span className="text-xs font-bold text-blue-600">WORD</span>
        </div>
      );
    }
    if (isExcel(file.name)) {
      return (
        <div className="w-full h-24 bg-green-50 flex flex-col items-center justify-center rounded-t-md border-b border-green-100">
          <svg className="h-10 w-10 text-green-600 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <span className="text-xs font-bold text-green-600">EXCEL</span>
        </div>
      );
    }
    if (isHTML(file.name)) {
      return (
        <div className="w-full h-24 bg-amber-50 flex flex-col items-center justify-center rounded-t-md border-b border-amber-100">
          <svg className="h-10 w-10 text-amber-600 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
          </svg>
          <span className="text-xs font-bold text-amber-600">HTML</span>
        </div>
      );
    }
    return (
      <div className="w-full h-24 bg-slate-50 flex flex-col items-center justify-center rounded-t-md border-b border-slate-100">
        <FileText className="h-10 w-10 text-slate-400 mb-1" />
        <span className="text-xs text-slate-500">{file.name.split('.').pop().toUpperCase()}</span>
      </div>
    );
  };

  return (
    <div className={`relative border rounded-md overflow-hidden bg-white shadow-sm ${overLimit ? 'border-red-400 ring-1 ring-red-300' : 'border-slate-200'}`}>
      {renderPreview()}
      <div className="p-2">
        <p className="text-xs text-slate-700 truncate font-medium" title={file.name}>{file.name}</p>
        <p className={`text-xs ${overLimit ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
          {formatSize(file.size)}{overLimit ? ` · твърде голям (макс ${limitMb} MB)` : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={handleRemove}
        className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow text-slate-500 hover:text-red-500 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function FilePickerInput({ files, onChange, label = 'Прикачи файлове' }) {
  const inputRef = useRef(null);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);

  useEffect(() => { fetchLimits().then(setLimits); }, []);

  const limitMbFor = (name) => {
    const n = (name || '').toLowerCase();
    return (limits.html_exts || DEFAULT_LIMITS.html_exts).some(e => n.endsWith(e))
      ? limits.html_mb : limits.default_mb;
  };

  const openPicker = () => {
    inputRef.current.click();
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;
    newFiles.forEach((f) => {
      const lim = limitMbFor(f.name);
      if (f.size > lim * 1024 * 1024) {
        toast.error(`„${f.name}" е твърде голям (макс ${lim} MB) и няма да се качи`);
      }
    });
    onChange([...files, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openPicker}
          className="flex items-center gap-2 text-slate-700 border-slate-300 hover:bg-slate-50"
        >
          <Paperclip className="h-4 w-4" />
          {label}
        </Button>
        {files.length > 0 && (
          <span className="text-xs text-slate-500">{files.length} файл{files.length !== 1 ? 'а' : ''} избрани</span>
        )}
        <span className="text-xs text-slate-400 ml-auto" data-testid="upload-size-hint">
          Макс. {limits.default_mb} MB на файл · HTML презентации до {limits.html_mb} MB
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {files.map((file, index) => (
            <FilePreviewThumb key={index} file={file} onRemove={removeFile} index={index} limitMb={limitMbFor(file.name)} />
          ))}
        </div>
      )}
    </div>
  );
}
